/**
 * @engine/vfx — VFX context types, built-in contexts + per-context settings.
 */
import type { ContextDefinition } from '@engine/graph';

/**
 * VFX context types (super-nodes).
 */
export type VFXContextType =
  | 'event'       // GPU Event source (triggers spawn)
  | 'spawn'       // Controls when/how many particles spawn
  | 'initialize'  // Sets initial particle attributes
  | 'update'      // Updates particles each frame
  | 'output';     // Renders particles

/**
 * Extended context definition with VFX-specific properties.
 */
export interface VFXContextDefinition extends ContextDefinition {
  /** Maximum number of blocks allowed (undefined = unlimited) */
  readonly maxBlocks?: number;
  
  /** Port type for connections (e.g., 'SpawnEvent') */
  readonly portType?: string;
  
  /** Whether this context can emit events (Update with trigger) */
  readonly canEmitEvents?: boolean;
  
  /** Context-level input fields (shown in header, like Capacity) */
  readonly contextInputs?: readonly {
    id: string;
    name: string;
    type: string;
    kind: string;
    defaultValue?: unknown;
    range?: { min: number; max: number };
    options?: readonly string[];
  }[];
}

/**
 * Built-in VFX contexts (super-nodes).
 * 
 * Flow: Event → Spawn (Start/Stop) → Initialize → Update → Output
 */
export const VFX_CONTEXTS: Record<VFXContextType, VFXContextDefinition> = {
  event: {
    id: 'vfx.event',
    name: 'GPU Event',
    color: '#E91E63',
    icon: 'zap',
    description: 'GPU Event source - triggers particle spawning',
    allowedNodeTypes: ['vfx.event.*'],
    required: false,
    order: -1,
    maxBlocks: 1, // Only Transit block allowed
    // No context-level inputs - Transit block handles Event input
    // Output: emits SpawnEvent to Spawn context
    flowOutputs: [
      { id: 'spawn_event', name: 'SpawnEvent', type: 'SpawnEvent', kind: 'flow' }
    ],
    portType: 'SpawnEvent',
  },
  spawn: {
    id: 'vfx.spawn',
    name: 'Spawn',
    color: '#4CAF50',
    icon: 'particle-spawn',
    description: 'Controls when and how many particles are spawned',
    allowedNodeTypes: ['vfx.spawn.*'],
    required: true,
    order: 0,
    // Inputs: Start and Stop accept Event type (NOT SpawnEvent)
    flowInputs: [
      { id: 'start', name: 'Start', type: 'Event', kind: 'flow' },
      { id: 'stop', name: 'Stop', type: 'Event', kind: 'flow' },
    ],
    // Output: SpawnEvent to Initialize
    flowOutputs: [
      { id: 'spawn_event', name: 'SpawnEvent', type: 'SpawnEvent', kind: 'flow' }
    ],
    emitsParticleEvent: true,
    maxBlocks: 1, // Spawn can only have ONE block!
    portType: 'SpawnEvent',
  },
  initialize: {
    id: 'vfx.initialize',
    name: 'Initialize Particle',
    color: '#2196F3',
    icon: 'particle-init',
    description: 'Sets initial values for each new particle',
    allowedNodeTypes: ['vfx.init.*', 'vfx.operator.*', 'vfx.attribute.*'],
    required: true,
    order: 1,
    // Context-level settings (shown in header)
    contextInputs: [
      { id: 'capacity', name: 'Capacity', type: 'uint', kind: 'data', defaultValue: 10000, range: { min: 1, max: 1000000 } },
      { id: 'boundsMode', name: 'Bounds', type: 'int', kind: 'data', defaultValue: 0, options: ['Manual', 'Auto', 'Recorded'] },
    ],
    // Input: SpawnEvent from Spawn
    flowInputs: [
      { id: 'spawn_event', name: 'SpawnEvent', type: 'SpawnEvent', kind: 'flow' }
    ],
    // Output: Particle data
    flowOutputs: [
      { id: 'particle', name: 'Particle', type: 'Particle', kind: 'flow' }
    ],
    portType: 'Particle',
  },
  update: {
    id: 'vfx.update',
    name: 'Update Particle',
    color: '#FF9800',
    icon: 'particle-update',
    description: 'Updates particles each frame (optional)',
    allowedNodeTypes: ['vfx.update.*', 'vfx.operator.*', 'vfx.attribute.*', 'vfx.trigger.*', 'vfx.output.*'],
    required: false, // Update is optional - can go Init → Output directly
    order: 2,
    // Input: Particle from Initialize
    flowInputs: [
      { id: 'particle', name: 'Particle', type: 'Particle', kind: 'flow' }
    ],
    // Output: Particle to Output
    flowOutputs: [
      { id: 'particle', name: 'Particle', type: 'Particle', kind: 'flow' },
    ],
    portType: 'Particle',
  },
  output: {
    id: 'vfx.output',
    name: 'Output',
    color: '#9C27B0',
    icon: 'particle-render',
    description: 'Renders particles',
    allowedNodeTypes: ['vfx.output.*'],
    required: true,
    order: 3,
    // Input: Particle from Update
    flowInputs: [
      { id: 'particle', name: 'Particle', type: 'Particle', kind: 'flow' }
    ],
    portType: 'Particle',
  },
};

/**
 * Event trigger mode for Update context.
 */
export type VFXEventTriggerMode = 
  | 'none'          // No event trigger
  | 'on_die'        // Trigger when particle dies
  | 'over_time'     // Trigger over time (rate-based)
  | 'over_distance' // Trigger over distance traveled
  | 'on_collide';   // Trigger on particle collision

/**
 * Event trigger configuration for Update context.
 */
export interface VFXEventTriggerConfig {
  /** Trigger mode */
  readonly mode: VFXEventTriggerMode;
  
  /** For on_die/on_collide: number of events per trigger */
  readonly count?: number;
  
  /** For over_time: events per second */
  readonly rate?: number;
  
  /** For over_distance: events per unit distance */
  readonly distanceRate?: number;
  
  /** Event ID to emit (for GPU Event system connections) */
  readonly eventId?: number;
}

/**
 * Settings for Initialize context.
 * Displayed as read-only header in the context.
 */
export interface VFXInitializeContextSettings {
  /** Maximum number of particles this system can hold (buffer size) */
  readonly capacity: number;
  
  /** How bounds are determined */
  readonly boundsMode: 'manual' | 'auto' | 'recorded';
  
  /** Manual bounds (when boundsMode is 'manual') */
  readonly bounds?: {
    readonly min: { x: number; y: number };
    readonly max: { x: number; y: number };
  };
}

/**
 * Settings for Update context.
 */
export interface VFXUpdateContextSettings {
  /** Event trigger configuration */
  readonly trigger?: VFXEventTriggerConfig;
}

/**
 * Combined context settings (Initialize or Update).
 */
export type VFXAnyContextSettings = VFXInitializeContextSettings | VFXUpdateContextSettings;

/**
 * Per-context settings keyed by context ID.
 */
export type VFXContextSettings = Record<string, VFXAnyContextSettings>;

/**
 * Get required flow connections for a valid graph.
 * Returns array of [fromContextType, toContextType] pairs.
 */
export function getRequiredContextFlows(): ReadonlyArray<readonly [VFXContextType, VFXContextType]> {
  return [
    ['spawn', 'initialize'],     // Spawn must connect to Init
    ['initialize', 'update'],    // Init must connect to Update
    ['update', 'output'],        // Update must connect to Output
  ] as const;
}
